/**
 * Machine fingerprint resolution (unit-05-machine-id-fingerprint).
 *
 * Single shared module every consumer must import to resolve a browser
 * `machineId` — do not let a future tool call `FingerprintJS.load()`
 * directly (ADR-11: this module is the one seam between the SPA and the
 * fingerprinting library).
 *
 * Open-source client-side tier only (ADR-4) — never import a `-pro`
 * package or call a server-confirmed/identification endpoint. The
 * open-source `@fingerprintjs/fingerprintjs` package computes its
 * `visitorId` entirely client-side from browser/device signals. The
 * installed 5.2.0 package defaults `monitoring: true`, which makes
 * `load()` fire a ~0.1%-probability telemetry XHR to FingerprintJS's own
 * `m1.openfpcdn.io` endpoint (see `dist/fp.esm.js`'s `monitor()`) — a real
 * external network call that would violate ADR-4's "no new backend network
 * dependency" and this unit's "no FingerprintJS network call" criterion.
 * `getMachineId()` explicitly disables it via `{ monitoring: false }` below
 * so no such call is possible.
 *
 * Computed once per session (unit-05 C2 / T-18): `getMachineId()` memoizes
 * the underlying computation at module scope so `FingerprintJS.load()` runs
 * at most once per page session, no matter how many callers ask for it.
 *
 * Why cache the in-flight PROMISE, not the resolved value (a deliberate
 * improvement over `src/lib/config.ts`'s `loadConfig()`/`getConfig()`
 * pair): `loadConfig()` guards with `if (cachedConfig) return cachedConfig`
 * — a synchronous check against the *resolved* value. Two callers that
 * both invoke `loadConfig()` before the first one's `await fetch(...)`
 * resolves will both see `cachedConfig` as `null` and both fire a
 * duplicate fetch. `getMachineId()` avoids that same race by caching the
 * *promise* returned by the first call, so a second caller mid-flight gets
 * the same in-flight promise instead of triggering a second
 * `FingerprintJS.load()`. This matters here because `client-options` (via
 * the user-menu "Client Options…" item) and Currency Bag Management can
 * both mount and call `getMachineId()` within the same page session,
 * potentially before either has resolved.
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Module-scope cache of the IN-FLIGHT PROMISE, not the resolved value (see
// header comment above for why this differs from src/lib/config.ts's
// pattern).
let machineIdPromise: Promise<string> | null = null;

export function getMachineId(): Promise<string> {
  machineIdPromise ??= FingerprintJS.load({ monitoring: false })
    .then((agent) => agent.get())
    .then((result) => result.visitorId);
  return machineIdPromise;
}

// Exposed for tests only — resets the module cache between test cases so
// each test gets an isolated call count against the mocked library.
export function _resetMachineIdCacheForTests(): void {
  machineIdPromise = null;
}
